import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

// Components
import VehicleImageGallery from '../components/vehicle/VehicleImageGallery';
import VehicleDetailsAccordion from '../components/vehicle/VehicleDetailsAccordion';
import SimilarVehicles from '../components/vehicle/SimilarVehicles';
import PaymentCalculator from '../components/PaymentCalculator';

// CSS
import './VehicleDetailPage.css';

const VehicleDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Güvenli bir şekilde useAuth hook'unu kullan
    const auth = useAuth() || {};
    const currentUser = auth.currentUser || null;

    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchVehicleDetails = async () => {
            try {
                setLoading(true);
                console.log(`Araç detayları için istek gönderiliyor: ID=${id}`);

                const response = await axios.get(`/api/vehicles/${id}`);

                if (response.data && response.data.success) {
                    let vehicleData = response.data.vehicle;

                    // Eksik alanları ekle - Önemli: Backend'den gelmeyen alanlar için boş nesneler oluştur
                    vehicleData.generalFeatures = vehicleData.generalFeatures || {};
                    vehicleData.engineDetails = vehicleData.engineDetails || {};
                    vehicleData.fuelConsumption = vehicleData.fuelConsumption || {};
                    vehicleData.dimensions = vehicleData.dimensions || {};
                    vehicleData.bodyCondition = vehicleData.bodyCondition || {};

                    setVehicle(vehicleData);
                    setError('');

                    // Sayfa başlığını güncelle
                    document.title = `${vehicleData.title || vehicleData.brand + ' ' + vehicleData.model} | Otoprime`;
                } else {
                    setError('Araç bilgileri yüklenirken bir hata oluştu');
                }
            } catch (error) {
                console.error('Araç detayları yükleme hatası:', error);

                if (error.response) {
                    const status = error.response.status;
                    const message = error.response.data?.message || 'Sunucu hatası';

                    if (status === 400) {
                        setError(`Geçersiz araç ID formatı: ${id}`);
                    } else if (status === 404) {
                        setError(`Araç bulunamadı: ${id}`);
                    } else {
                        setError(`Sunucu hatası (${status}): ${message}`);
                    }
                } else if (error.request) {
                    setError('Sunucu yanıt vermiyor. Lütfen daha sonra tekrar deneyin.');
                } else {
                    setError(`İstek hatası: ${error.message}`);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchVehicleDetails();

        // Component unmount olduğunda sayfa başlığını sıfırla
        return () => {
            document.title = 'Otoprime';
        };
    }, [id]);

    const handleEditClick = () => {
        navigate(`/vehicles/edit/${id}`);
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <Alert.Heading>Hata</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <button
                            className="btn btn-outline-danger"
                            onClick={() => navigate('/vehicles')}
                        >
                            Araçlar Listesine Dön
                        </button>
                    </div>
                </Alert>
            </Container>
        );
    }

    if (!vehicle) {
        return (
            <Container className="py-5">
                <Alert variant="warning">
                    <Alert.Heading>Araç Bulunamadı</Alert.Heading>
                    <p>İstediğiniz araç bulunamadı veya kaldırılmış olabilir.</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <button
                            className="btn btn-outline-warning"
                            onClick={() => navigate('/vehicles')}
                        >
                            Araçlar Listesine Dön
                        </button>
                    </div>
                </Alert>
            </Container>
        );
    }

    // Araç için resim URL'si oluştur
    const vehicleImages = [];
    // Ana resim varsa ekle
    if (vehicle.imageUrl && vehicle.imageUrl !== '/uploads/vehicles/default.jpg') {
        const fullUrl = vehicle.imageUrl.startsWith('http')
            ? vehicle.imageUrl
            : `${vehicle.imageUrl}`;
        vehicleImages.push(fullUrl);
    }

    // Diğer resimler varsa ekle
    if (vehicle.images && vehicle.images.length > 0) {
        vehicle.images.forEach(img => {
            // Default resmi atla
            if (img && img !== '/uploads/vehicles/default.jpg') {
                const fullUrl = img.startsWith('http')
                    ? img
                    : `${img}`;

                // URL zaten eklenmemişse ekle (tekrarı önle)
                if (!vehicleImages.includes(fullUrl)) {
                    vehicleImages.push(fullUrl);
                }
            }
        });
    }

    return (
        <Container className="py-4 vehicle-detail-container">
            {/* Üst Başlık Kısmı */}
            <div className="vehicle-header">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h1 className="vehicle-title">{vehicle.title}</h1>
                        <div className="vehicle-subtitle">
                            {vehicle.brand} {vehicle.model} • {vehicle.year} • {vehicle.transmission} • {vehicle.fuelType}
                        </div>
                    </div>
                    {/* Modify this part in the VehicleDetailPage.js file */}
                    <div className="vehicle-price-container">
                        <div className="vehicle-price">{vehicle.price?.toLocaleString()} ₺</div>
                        {vehicle.status === 'sold' ? (
                            <div className="vehicle-price-note">Satıldı</div>
                        ) : (
                            <div className="vehicle-price-note-negotiable">
                                <i className="bi bi-tags-fill me-1"></i>
                                Pazarlık Payı Vardır
                            </div>
                        )}
                    </div>
                </div>

                <div className="vehicle-badges mb-4">
                    <Badge bg="secondary" className="vehicle-badge">{vehicle.year}</Badge>
                    <Badge bg="secondary" className="vehicle-badge">{vehicle.mileage?.toLocaleString()} km</Badge>
                    <Badge bg="secondary" className="vehicle-badge">{vehicle.fuelType}</Badge>
                    <Badge bg="secondary" className="vehicle-badge">{vehicle.transmission}</Badge>
                    <Badge bg="secondary" className="vehicle-badge">{vehicle.color}</Badge>
                    {vehicle.location && (
                        <Badge bg="secondary" className="vehicle-badge">
                            {vehicle.location.city}, {vehicle.location.district}
                        </Badge>
                    )}
                </div>
            </div>

            <Row>
                {/* Sol Kolon: Araç Bilgileri */}
                <Col lg={8}>
                    {/* Araç Galerisi */}
                    <VehicleImageGallery images={vehicleImages} />

                    {/* Araç sahibiyse düzenleme butonu */}
                    {currentUser && vehicle.owner && currentUser._id === vehicle.owner._id && (
                        <div className="mb-4">
                            <Button
                                variant="warning"
                                onClick={handleEditClick}
                            >
                                İlanı Düzenle
                            </Button>
                        </div>
                    )}

                    {/* Araç Detayları - Akordiyon */}
                    <VehicleDetailsAccordion vehicle={vehicle} />

                    {/* Benzer Araçlar */}
                    <SimilarVehicles currentVehicle={vehicle} />
                </Col>

                {/* Sağ Kolon: Aylık Ödeme Hesaplama */}
                <Col lg={4}>
                    {/* Aylık Ödeme Hesaplama - Tek parça olarak kullan */}
                    <Card className="mb-4 shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Aylık Ödeme Tutarınız</h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <PaymentCalculator
                                initialAmount={vehicle.price || 100000}
                                readOnlyAmount={false}
                            />
                        </Card.Body>
                    </Card>

                    {/* QR Kod ve Paylaşım */}
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">İlanı Paylaş</h5>
                        </Card.Header>
                        <Card.Body className="text-center">
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.href)}`}
                                alt="QR Kod"
                                className="qr-code mb-3"
                            />
                            <div className="share-buttons">
                                <Button variant="outline-primary" size="sm" className="mx-1">
                                    <i className="bi bi-facebook"></i> Facebook
                                </Button>
                                <Button variant="outline-info" size="sm" className="mx-1">
                                    <i className="bi bi-twitter"></i> Twitter
                                </Button>
                                <Button variant="outline-success" size="sm" className="mx-1">
                                    <i className="bi bi-whatsapp"></i> WhatsApp
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default VehicleDetailPage;